import { FC, useCallback } from 'react';
import styled, { css } from 'styled-components';
import { IconProps } from '../../icon/createIconComponent';
import { Button, ButtonColor, ButtonProps } from '../../input/Button';
import { DataTestId } from '../../layout/DataTestIdWrapper';
import { Flex } from '../../layout/Flex';
import { Typography } from '../../typography/Typography';

const Container = styled(Button)
    .withConfig({ displayName: 'Container' })
    .attrs((props) => ({
        padding: '12px 16px',
        width: '100%',
        height: 'auto',
        textTransform: 'none',
        textAlign: 'left',
        isHoverEnabled: false,
        onClick: props.onClick,
    }))`
    border-radius: 16px;
    box-shadow: 0px 0px 4px 0px var(--control-primary-blur-background);
    ${(props) =>
        !props.color &&
        css`
            background-color: var(--background-primary);
        `}
`;

interface ToastProps {
    message: string;
    subtitle?: string;
    onClick?: VoidFunction;
    onRemove?: VoidFunction;
    actionText?: string;
    icon?: FC<IconProps>;
    color?: ButtonColor;
    minWidth?: ButtonProps['minWidth'];
}

export const Toast: FC<ToastProps> = ({
    message,
    subtitle,
    onClick: onClickBase,
    actionText,
    onRemove,
    icon: IconComponent,
    color,
    minWidth,
}) => {
    const onClick = useCallback(() => {
        onClickBase?.();
        onRemove?.();
    }, [onClickBase, onRemove]);

    const textColor = color === 'primary' ? 'var(--control-primary-color)' : undefined;
    const subtitleColor =
        color === 'primary' ? 'var(--color-secondary-invert)' : 'var(--color-secondary)';
    const iconTintColor =
        color === 'primary' ? 'var(--control-primary-color)' : 'var(--color-primary)';

    return (
        <Container
            onClick={onClick}
            color={color}
            minWidth={minWidth}
            dataTestId={DataTestId.MESSAGE_TOAST}
        >
            <Flex
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                width="100%"
                spacing={2}
            >
                {IconComponent ? <IconComponent size={20} color={iconTintColor} /> : null}
                <Flex
                    direction="row"
                    alignItems="baseline"
                    spacing={2}
                    width="100%"
                    justifyContent="space-between"
                >
                    <Flex direction="column" mr="auto">
                        <Typography variant="body" color={textColor}>
                            {message}
                        </Typography>
                        {subtitle ? (
                            <Typography variant="caption" color={subtitleColor}>
                                {subtitle}
                            </Typography>
                        ) : null}
                    </Flex>
                    {actionText ? (
                        <Typography
                            textTransform="uppercase"
                            variant="caption"
                            weight="bold"
                            mt="2px" // uppercase визуально вызывает смещение наверх
                            alignSelf="center"
                            color={textColor}
                        >
                            {actionText}
                        </Typography>
                    ) : null}
                </Flex>
            </Flex>
        </Container>
    );
};
